import React from "react";
import { Map, CloudSun, ShieldAlert, PlaneTakeoff, Download, Share2, MoreVertical } from "lucide-react";
import { usePWAInstall } from "../../hooks/usePWA";

// NOTE: `image` is empty on purpose — en attente des vraies captures d'écran de l'app.
// Dès qu'elles sont fournies, renseigner l'URL ici ; le cadre bascule automatiquement
// de l'icône placeholder vers l'<img> (lazy-loaded, avec texte alternatif).
const SCREENS = [
  {
    key: "map",
    icon: Map,
    title: "Votre itinéraire sur une carte",
    desc: "Chaque jour a sa couleur, chaque activité son point. Vous savez toujours où aller ensuite.",
    image: "https://customer-assets-eiarnc6j.emergentagent.net/job_voyage-ia-1/artifacts/5a298faa1364c3af_Screenshot_20261101_180913_Chrome.webp",
    alt: "Capture d'écran de l'itinéraire de voyage affiché sur une carte, avec un code couleur par jour",
  },
  {
    key: "live",
    icon: CloudSun,
    title: "Mode Sur Place",
    desc: "Le programme du jour, la météo, les numéros utiles. Tout ce qu'il faut, au bon moment.",
    image: "https://customer-assets-eiarnc6j.emergentagent.net/job_voyage-ia-1/artifacts/6aa15a506d4cef21_Screenshot_20261101_181106_Chrome.webp",
    alt: "Capture d'écran du programme de voyage jour par jour dans l'application",
  },
  {
    key: "scam",
    icon: ShieldAlert,
    title: "Radar anti-arnaques",
    desc: "Les pièges classiques de votre destination, pour ne pas vous faire avoir.",
    image: "https://customer-assets-eiarnc6j.emergentagent.net/job_voyage-ia-1/artifacts/5834b5c7fc54a86f_Screenshot_20261101_180930_Chrome.webp",
    alt: "Capture d'écran du Radar anti-arnaques listant les pièges classiques à destination",
  },
  {
    key: "flight",
    icon: PlaneTakeoff,
    title: "Suivi de vol en temps réel",
    desc: "Retard, porte, terminal : vous êtes prévenu.",
    image: "https://customer-assets-eiarnc6j.emergentagent.net/job_voyage-ia-1/artifacts/d92ab7153adcbf42_Screenshot_20261101_181218_Chrome.webp",
    alt: "Capture d'écran du suivi de vol en temps réel avec statut, porte et terminal",
  },
];

function PhoneScreen({ screen }) {
  const Icon = screen.icon;
  return (
    <div
      className="relative mx-auto w-[150px] sm:w-[170px] aspect-[9/19] bg-[#2B2623] p-[8px]"
      style={{ borderRadius: "22px", boxShadow: "2px 6px 18px rgba(43,38,35,0.18)" }}
    >
      <div className="absolute left-1/2 -translate-x-1/2 top-[8px] w-10 h-[5px] bg-[#2B2623] rounded-full z-10" />
      <div className="w-full h-full overflow-hidden bg-[#F9F6F0] flex items-center justify-center" style={{ borderRadius: "16px" }}>
        {screen.image ? (
          <img
            src={screen.image}
            alt={screen.alt}
            loading="lazy"
            className="w-full h-full object-cover object-top"
          />
        ) : (
          <div className="flex flex-col items-center gap-2 px-4 text-center">
            <Icon size={26} strokeWidth={1.3} className="text-[#C17F59]" />
            <p className="text-xs text-[#8B5E34] italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
              Capture à venir
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function LandingAppPreview() {
  const { isInstallable, isInstalled, install } = usePWAInstall();
  const showInstallButton = isInstallable && !isInstalled;

  return (
    <section className="py-20 border-t border-[#DCD4C6]" data-testid="app-preview-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-14">
          <p className="section-label mb-3">L'application</p>
          <h2
            className="text-4xl sm:text-5xl mb-4"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}
          >
            Votre voyage tient dans votre poche
          </h2>
          <p className="text-base lg:text-lg text-[#5C554E] max-w-2xl mx-auto" style={{ fontFamily: "'Lora', Georgia, serif" }}>
            Voyage-r n'est pas un PDF. C'est une appli qui vous accompagne avant et pendant le voyage, même sans réseau.
          </p>
        </div>

        {/* 4 cartes — grille sur desktop, carrousel défilable sur mobile */}
        <div
          className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-5 px-5 sm:mx-0 sm:px-0 lg:grid lg:grid-cols-4 lg:gap-6 lg:overflow-visible lg:pb-0"
          data-testid="app-preview-cards"
        >
          {SCREENS.map((screen, i) => (
            <div
              key={screen.key}
              className="card-surface flex-shrink-0 w-[78%] sm:w-[48%] lg:w-auto snap-center animate-fade-in-up"
              style={{ animationDelay: `${i * 100}ms` }}
              data-testid={`app-preview-card-${screen.key}`}
            >
              <PhoneScreen screen={screen} />
              <h3
                className="mt-5 mb-2 text-lg text-center"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}
              >
                {screen.title}
              </h3>
              <p className="text-sm text-[#5C554E] text-center leading-relaxed" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                {screen.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Encadré installation PWA */}
        <div className="card-surface mt-14 max-w-3xl mx-auto text-center" data-testid="app-install-box">
          <h3 className="mb-2 text-lg" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>
            Installer l'appli en 10 secondes
          </h3>
          <p className="text-sm text-[#5C554E] mb-5" style={{ fontFamily: "'Lora', Georgia, serif" }}>
            Pas besoin de store : Voyage-r s'installe directement depuis votre navigateur.
          </p>
          <div className={`grid sm:grid-cols-2 gap-4 text-left ${showInstallButton ? "mb-6" : ""}`}>
            <div
              className="flex items-start gap-3 px-4 py-3 border border-[#DCD4C6] bg-[#F9F6F0]"
              style={{ borderRadius: "2px" }}
              data-testid="install-instructions-ios"
            >
              <Share2 size={16} strokeWidth={1.5} className="text-[#C17F59] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#5C554E]" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                <strong className="text-[#2B2623]">iPhone — </strong>
                Ouvrez Voyage-r dans Safari → bouton Partager → Sur l'écran d'accueil.
              </p>
            </div>
            <div
              className="flex items-start gap-3 px-4 py-3 border border-[#DCD4C6] bg-[#F9F6F0]"
              style={{ borderRadius: "2px" }}
              data-testid="install-instructions-android"
            >
              <MoreVertical size={16} strokeWidth={1.5} className="text-[#C17F59] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#5C554E]" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                <strong className="text-[#2B2623]">Android — </strong>
                Ouvrez Voyage-r dans Chrome → menu ⋮ → Installer l'application.
              </p>
            </div>
          </div>
          {showInstallButton && (
            <button
              onClick={install}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="app-preview-install-btn"
            >
              <Download size={15} strokeWidth={1.5} />
              Installer Voyage-r
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
